import { useMemo, useContext } from "react";
import classNames from "classnames";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import { useFlags } from "@stadium/services/launch-darkly";
import ZendeskIconPositionAdjuster from "@stadium/services/zendesk/icon-position-adjuster";
import { ZENDESK_POSITION_BOTTOM_DEFAULT } from "@stadium/services/zendesk/constants";

import { useWorkspaceContext } from "@stadium/hooks/lib/workspace";

import { LOGIN_CALLBACK_ROUTE, useAuth0V2 } from "@stadium/services/auth0";
import { DASHBOARD, MY_WORKSPACE_SLUG } from "@stadium/constants";

import { getImpersonatedUser } from "@stadium/helpers/impersonation";

import { useCurrentUserContext } from "@stadium/hooks/lib/use-current-user";
import useLogout from "@hooks/use-logout";
import { useAnnouncementBarContext } from "@stadium/shared/src/contexts/announcement-bar";

import StadiumHeader from "@stadium/shared/header";
import StartButton from "@stadium/shared/src/components/recognise-widget/start-button";

import { isAppGenericAnnouncementBarFlagEnabled } from "@stadium/ui/src/helpers/app-generic-banner";

import {
  customDomainDashboardConfigFlag,
  workspaceSidebarFlag,
  appGenericAnnouncementBarFlag,
  quickRecogniseWidgetFlag,
  kudosProgramPresentFlag,
} from "@stadium/services/launch-darkly/constants";

import styles from "./main.module.scss";

const DEFAULT_OBJ = {};

const Header = ({ additionalLoginOptions = DEFAULT_OBJ, showHeaderNavLinks }) => {
  const router = useRouter();
  const { isClosed: isAppGenericBannerClosed } = useAnnouncementBarContext();
  const { query } = router;
  const { workspaceSlug } = query;

  const impersonatedUser = getImpersonatedUser();
  const { isDesktopDevice } = useContext(CurrentDeviceContext);

  const {
    [customDomainDashboardConfigFlag]: customDomainDashboardConfigFlagData,
    [workspaceSidebarFlag]: workspaceSidebarFlagEnabled,
    [appGenericAnnouncementBarFlag]: appGenericAnnouncementBarFlagValue,
    [quickRecogniseWidgetFlag]: quickRecogniseWidgetFlagValue,
    [kudosProgramPresentFlag]: kudosProgramPresentFlagValue,
  } = useFlags();

  const { currentUserData } = useCurrentUserContext();
  const {
    currentWorkspaceUser,
    workspace,
    teamsWithoutDefault,
    teams,
    isActivePackageUser,
    workspacePermissions,
    workspaces,
  } = useWorkspaceContext();

  const { quick_recognize: quickRecognizeOrderEnabled } = workspacePermissions?.orders || {};
  const showQuickRecogniseBtn =
    quickRecognizeOrderEnabled ||
    (kudosProgramPresentFlagValue && currentWorkspaceUser?.kudosProgramPresent);

  const appGenericAnnouncementBarFlagEnabled = isAppGenericAnnouncementBarFlagEnabled(
    appGenericAnnouncementBarFlagValue,
    DASHBOARD,
    isActivePackageUser,
  );

  const {
    name,
    firstName,
    lastName,
    email,
    spreeId: id,
    avatarUrl,
    workspacesByDomainExists,
    fullName,
    profile,
  } = currentUserData;

  const { user, isAuthenticated, isLoading } = useAuth0V2();

  const { workspacePoints } = currentWorkspaceUser || DEFAULT_OBJ;

  const { logoutAction } = useLogout();

  const onWorkspaceSelection = (selectedWorkspaceSlug, currentPath) => {
    if (workspaceSlug === MY_WORKSPACE_SLUG && !currentPath.includes(MY_WORKSPACE_SLUG)) {
      return `/${selectedWorkspaceSlug}/${currentPath}`;
    }
    return currentPath.replace(workspaceSlug, selectedWorkspaceSlug);
  };

  const profileData = useMemo(
    () => ({
      authUsername: user?.name,
      name,
      firstName,
      lastName,
      email,
      avatarUrl,
      id,
      workspacesByDomainExists,
      fullName,
      defaultWorkspace: profile?.defaultWorkspace,
      activeWorkspaceSlug: workspaceSlug,
      workspaces,
      onWorkspaceSelection,
    }),
    [
      email,
      id,
      name,
      user,
      avatarUrl,
      workspacesByDomainExists,
      profile,
      workspaces,
      workspaceSlug,
      fullName,
    ],
  );

  const userDetails = useMemo(
    () => ({
      teams,
      workspacePoints,
      isPersonalWorkspace: workspace?.isPersonalWorkspace,
      teamsWithoutDefaultPresent: teamsWithoutDefault?.length,
    }),
    [teams, workspacePoints, workspace],
  );

  return (
    <>
      <StadiumHeader
        profileData={profileData}
        logout={logoutAction}
        isWeglotDropDown
        isAuthenticated={isAuthenticated}
        loginOptions={{
          loginCallback: `/shops${LOGIN_CALLBACK_ROUTE}`,
          ...additionalLoginOptions,
        }}
        logoDetails={customDomainDashboardConfigFlagData}
        isLoading={isLoading}
        className={`${styles.topStickyNav} ${styles.topFixedNav}`}
        showHeaderNavLinks={showHeaderNavLinks}
        showProfileDropdown={workspaceSidebarFlagEnabled}
        userDetails={userDetails}
        application={DASHBOARD}
        isActivePackageUser={isActivePackageUser}
        showRecogniseButton={showQuickRecogniseBtn}
        personalWorkspaceNudge={workspace?.isPersonalWorkspace}
      />
      <div
        className={classNames(styles.fixedNavEmptyArea, {
          [styles.fixedNavEmptyAreaImpersonate]:
            impersonatedUser && !appGenericAnnouncementBarFlagEnabled,
          [styles.fixedNavEmptyAreaWithAppGenericAnnouncementBar]:
            appGenericAnnouncementBarFlagEnabled && !impersonatedUser && !isAppGenericBannerClosed,
          [styles.fixedNavEmptyAreaWithAppGenericAnnouncementBarAndImpersonate]:
            appGenericAnnouncementBarFlagEnabled && impersonatedUser,
        })}
      />
      <ZendeskIconPositionAdjuster
        bottomOffset={isDesktopDevice ? ZENDESK_POSITION_BOTTOM_DEFAULT : "-120px"}
        adjustOnUnmount={false}
      >
        {!isDesktopDevice && quickRecogniseWidgetFlagValue && showQuickRecogniseBtn && (
          <div className={styles.recogniseFab}>
            <StartButton />
          </div>
        )}
      </ZendeskIconPositionAdjuster>
    </>
  );
};

export default Header;
